import React, { useMemo, useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import Login from './components/Login';
import Dashboard from './components/Dashboard';
import Projects from './components/Projects';
import ProjectOverview from './components/ProjectOverview';
import Tasks from './components/Tasks';
import Approvals from './components/Approvals';
import Users from './components/Users';
import Header from './components/Header';
import Settings from './components/Settings';
import Calendar from './components/Calendar';
import PMBBoard from './components/PMBBoard';
import ProjectRequests from './components/ProjectRequests';
import Clients from './components/Clients';
import ClientIssues from './components/ClientIssues';
import Feedback from './components/Feedback';
import ClientFeedback from './components/ClientFeedback';
import api from './api/axios';
import ToastHost from './components/ToastHost';
import Sidebar from './components/Sidebar';
import './index.css';

function App() {
  const [user, setUser] = useState(null);
  const [theme, setTheme] = useState('light');
  const [settings, setSettings] = useState({ company_name: 'Pipeline Management', logo_file: null, logo_url: null });
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);

  const isAdmin = useMemo(() => user?.role === 'ADMIN', [user?.role]);
  const isClient = useMemo(() => user?.role === 'CLIENT', [user?.role]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    const savedTheme = localStorage.getItem('theme') || 'light';
    
    if (token && userData) {
      setUser(JSON.parse(userData));
    }
    
    setTheme(savedTheme);
    document.documentElement.setAttribute('data-theme', savedTheme);
  }, []);

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 1024;
      setIsMobile(mobile);
      if (!mobile) {
        setSidebarOpen(true);
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const loadSettings = async () => {
      try {
        const res = await api.get('/api/settings');
        const logoUrl = res.data?.logo_file ? `/uploads/${res.data.logo_file}` : null;
        const next = { ...res.data, logo_url: logoUrl };
        setSettings(next);
        document.title = next.company_name || 'Pipeline Management';
      } catch (e) {
        document.title = 'Pipeline Management';
      }
    };

    loadSettings();
  }, []);

  const toggleTheme = () => {
    const newTheme = theme === 'light' ? 'dark' : 'light';
    setTheme(newTheme);
    localStorage.setItem('theme', newTheme);
    document.documentElement.setAttribute('data-theme', newTheme);
  };

  const login = (userData, token) => {
    setUser(userData);
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
  };

  const logout = () => {
    setUser(null);
    localStorage.removeItem('token');
    localStorage.removeItem('user');
  };

  return (
    <ToastHost>
      <Router>
        <div className="App">
          {user ? (
            <>
              <Header
                user={user}
                logout={logout}
                toggleTheme={toggleTheme}
                theme={theme}
                settings={settings}
                sidebarOpen={sidebarOpen}
                onToggleSidebar={() => {
                  if (!isMobile) return;
                  setSidebarOpen((v) => !v);
                }}
                onCloseSidebar={() => {
                  if (!isMobile) return;
                  setSidebarOpen(false);
                }}
              />
              <div className={`app-shell app-shell-grid${sidebarOpen ? ' app-shell-open' : ''}`}>
                {isMobile && sidebarOpen ? (
                  <div
                    className="app-sidebar-backdrop"
                    role="presentation"
                    onClick={() => setSidebarOpen(false)}
                  />
                ) : null}
                <Sidebar user={user} onNavigate={() => setSidebarOpen(false)} />

                <main className="app-main">
                  <div className="container">
                    <Routes>
                      <Route path="/dashboard" element={<Dashboard user={user} />} />
                      <Route path="/projects" element={<Projects user={user} />} />
                      <Route path="/projects/:projectId" element={<ProjectOverview user={user} />} />

                      <Route path="/tasks" element={isClient ? <Navigate to="/dashboard" /> : <Tasks user={user} />} />
                      <Route path="/approvals" element={isClient ? <Navigate to="/dashboard" /> : <Approvals user={user} />} />
                      <Route path="/project-requests" element={isClient ? <Navigate to="/dashboard" /> : <ProjectRequests user={user} />} />
                      <Route path="/pmb/:projectId" element={isClient ? <Navigate to="/dashboard" /> : <PMBBoard user={user} />} />

                      <Route path="/calendar" element={<Calendar user={user} />} />
                      <Route path="/client-issues" element={<ClientIssues user={user} />} />
                      <Route path="/feedback" element={isClient ? <ClientFeedback user={user} /> : <Feedback user={user} />} />

                      {(user.role === 'ADMIN' || user.role === 'BDM') && (
                        <>
                          <Route path="/users" element={<Users user={user} />} />
                        </>
                      )}

                      {user.role === 'ADMIN' && (
                        <>
                          <Route path="/clients" element={<Clients user={user} />} />
                          <Route
                            path="/settings"
                            element={<Settings user={user} settings={settings} onSettingsUpdated={(s) => {
                              const logoUrl = s?.logo_file ? `/uploads/${s.logo_file}` : null;
                              const next = { ...s, logo_url: logoUrl };
                              setSettings(next);
                              document.title = next.company_name || 'Pipeline Management';
                            }} />}
                          />
                        </>
                      )}

                      {user.role === 'BDM' && (
                        <>
                          <Route path="/clients" element={<Clients user={user} />} />
                        </>
                      )}
                      <Route path="/" element={<Navigate to="/dashboard" />} />
                      <Route path="*" element={<Navigate to="/dashboard" />} />
                    </Routes>
                  </div>
                </main>
              </div>
            </>
          ) : (
            <Login onLogin={login} settings={settings} />
          )}
        </div>
      </Router>
    </ToastHost>
  );
}

export default App;
